/* DiFluid OS — Learn.

   Two things live here:

   1. The catalog. Every course, tool guide and device guide, with
      the signals the ranker needs. Add an entry and it appears in the
      right section, ranked, with no other change.

   2. The ranker. What a reader sees first is decided at render time
      from four signals, not by the order they are typed in below.

   ── The ranker ─────────────────────────────────────────────────
   Five terms, summed. Every term is 0..1 before its weight.

     role     0.34   how much this reader's job needs this guide
     bag      0.26   the thing is in the cart, or shares its room
     owned    0.14   they have opened the tool, or bought the device
     trade    0.16   how much of the business this product actually is
     pin      0.10   editorial: what we want a newcomer to open first

   A guide for something IN THE BAG is also pinned above its section
   and flagged, because "you are buying this" beats any score.

   `trade` is share of net sales, DiFluid Shopify, 2020-01 to 2026-06.
   It is NOT a conversion rate — Shopify does not expose per-product
   conversion to the storefront, and inventing one would be worse than
   using a real number. When per-product conversion becomes available,
   set OS_CONV = {slug: 0..1} from Liquid and it replaces this table
   without touching the weights.
   ─────────────────────────────────────────────────────────────── */
window.OS_LEARN = (function () {
  "use strict";
  function T(k, en) { var t = window.OS_T; return (t && typeof t[k] === "string" && t[k]) || en; }

  var FAMILY = {
    producer: "green", exporter: "green", importer: "green", q: "green",
    roaster: "roast", home: "roast",
    cafe: "bar", hobby: "bar",
    educator: "teach",
    competitor: "comp"
  };

  /* Share of net sales, normalized against the strongest product.
     Square-rooted so the top three do not flatten everything else. */
  var TRADE_SHARE = {
    "omix-plus": 26.17,
    "omni": 21.69,
    "r2-extract": 18.39,
    "brew-control-ti": 12.57,   /* all Brew Control System variants */
    "airwave": 6.53,
    "microbalance-ti": 4.44,    /* Ti + original */
    "coffmeter-a1": 1.37,
    "omniflux": 1.36,
    "coffmeter-m1": 1.32,
    "moment": 0.96,
    "coffmeter-w1": 0.76,
    "coaster": 0.35
  };

  var TRADE = (function () {
    var out = {}, max = 0, k;
    for (k in TRADE_SHARE) if (TRADE_SHARE[k] > max) max = TRADE_SHARE[k];
    for (k in TRADE_SHARE) out[k] = Math.sqrt(TRADE_SHARE[k] / max);
    return out;
  })();

  var W = { role: 0.34, bag: 0.26, owned: 0.14, trade: 0.16, pin: 0.10 };

  /* ── Catalog ─────────────────────────────────────────────────

     kind   "course" | "tool" | "device"
     roles  role id or family -> 0..1, how much this reader needs it
     for    product slugs this guide is about (drives bag + trade)
     room   farm | roast | brew — used for the softer bag match
     guide  key into OS_GUIDES when the page exists, else null
     state  "ready" | "soon"
     ────────────────────────────────────────────────────────────── */

  /* The competitor brews and roasts, so a guide is worth as much to
     them as it is to whichever of those two it already serves best.
     Derived rather than typed out twenty-five times, which also means
     a guide added later gets a competitor weight the moment it gets a
     bar or a roast one. Anything set explicitly wins. */
  function addCompWeights(items) {
    items.forEach(function (it) {
      if (!it.roles || it.roles.comp != null) return;
      it.roles.comp = Math.max(
        it.roles.bar || 0,
        it.roles.roast || 0,
        (it.roles.hobby || 0) * 0.9
      );
    });
    return items;
  }

  var ITEMS = [

    /* ── Courses ─────────────────────────────────── */
    {
      id: "particle",
      kind: "course",
      title: T("learn.title.particle_analyzer", "Particle Analyzer"),
      sub: T("learn.particle.sub", "Read a grind distribution, and know when it moved"),
      meta: T("learn.lessons", "{n} lessons").replace("{n}", 6),
      icon: "particle-analyzer",
      room: "brew",
      for: ["omni", "coaster"],
      guide: "particle-course",
      state: "ready",
      pin: 1.0,
      roles: { bar: 1.0, hobby: 1.0, roast: 0.75, q: 0.9, green: 0.35, teach: 0.9 }
    },
    {
      id: "brewcontrol",
      kind: "course",
      title: T("learn.title.taking_control_of_your_brew", "Taking control of your brew"),
      sub: T("learn.brewcontrol.sub", "Dose, yield, TDS and what each one tells you about the cup"),
      meta: T("learn.lessons", "{n} lessons").replace("{n}", 5),
      icon: "brew-controller",
      room: "brew",
      for: ["r2-extract", "microbalance-ti", "brew-control-ti"],
      guide: "brew-course",
      state: "ready",
      pin: 0.8,
      roles: { bar: 1.0, roast: 0.7, q: 0.8, teach: 0.95, green: 0.4 }
    },
    {
      id: "colour",
      kind: "course",
      title: T("learn.title.measuring_roast_color", "Measuring roast color"),
      sub: T("learn.colour.sub", "Agtron, the roast delta between whole beans and grounds, and what it is not"),
      meta: T("learn.lessons", "{n} lessons").replace("{n}", 4),
      icon: "roast-color",
      room: "roast",
      for: ["omni", "coffmeter-a1", "omniflux"],
      guide: "colour-manual",
      state: "ready",
      pin: 0.7,
      roles: { roast: 1.0, q: 0.85, green: 0.6, bar: 0.5, teach: 0.8 }
    },
    {
      id: "grindprofile",
      kind: "course",
      title: T("learn.title.profiling_a_grinder", "Profiling a grinder"),
      sub: T("learn.grindprofile.sub", "Build a curve for your burrs, then convert between them"),
      meta: T("learn.lessons", "{n} lessons").replace("{n}", 5),
      icon: "grinder-manager",
      room: "brew",
      for: ["omni"],
      guide: "grinder-course",
      state: "ready",
      pin: 0.5,
      roles: { hobby: 1.0, bar: 0.9, roast: 0.6, q: 0.6, teach: 0.6, green: 0.2 }
    },
    {
      id: "greengrading",
      kind: "course",
      /* Published, as the article "5 Green Bean Metrics That Quietly
         Decide Your Cup Score" — which is exactly the four listed
         below, so this entry points at it rather than waiting for a
         course. Retitled off "graded by number": the measurements
         have names and the sub-line already gives them. */
      title: T("learn.title.the_five_green_measurements", "The five green measurements"),
      sub: T("learn.greengrading.sub", "Moisture, water activity, density and screen size"),
      meta: T("learn.meta.article", "Article"),
      icon: "bean-manager",
      room: "farm",
      for: ["omix-plus", "coffmeter-m1", "coffmeter-w1"],
      guide: "green-metrics",
      state: "ready",
      pin: 0.6,
      roles: { green: 1.0, q: 1.0, roast: 0.6, teach: 0.7, bar: 0.15 }
    },

    /* ── Tool guides (CoffeeOS) ──────────────────── */
    {
      id: "t-particle",
      handle: "particle-analyzer",
      kind: "tool", cat: "brew",
      title: T("learn.title.particle_analyzer", "Particle Analyzer"),
      sub: T("learn.t_particle.sub", "Measure grind size distribution: D10, D50, D90 and how even the grind is."),
      icon: "particle-analyzer",
      room: "brew",
      for: ["omni", "coaster"],
      guide: "particle-course",
      state: "ready",
      pin: 0.9,
      roles: { bar: 1.0, hobby: 1.0, roast: 0.7, q: 0.9, green: 0.3, teach: 0.85 }
    },
    {
      id: "t-roastcolor",
      handle: "roast-color-analyzer",
      kind: "tool", cat: "roast",
      title: T("learn.title.roast_color_analyzer", "Roast Color Analyzer"),
      sub: T("learn.t_roastcolor.sub", "Compares the color of whole beans and grounds and shows the roast delta."),
      icon: "roast-color",
      room: "roast",
      for: ["omni", "coffmeter-a1", "omniflux"],
      guide: "colour-manual",
      state: "ready",
      pin: 0.85,
      roles: { roast: 1.0, q: 0.9, green: 0.55, bar: 0.45, teach: 0.7 }
    },
    {
      id: "t-brewcontroller",
      handle: "brew-controller",
      kind: "tool", cat: "brew",
      title: T("learn.title.brew_controller", "Brew Controller"),
      sub: T("learn.t_brewcontroller.sub", "Plots extraction against strength so you can see how a brew compares with your target."),
      icon: "brew-controller",
      room: "brew",
      for: ["r2-extract", "brew-control-ti"],
      guide: "brew-course",
      state: "ready",
      pin: 0.85,
      roles: { bar: 1.0, roast: 0.7, q: 0.95, teach: 0.9, green: 0.35 }
    },
    {
      id: "t-brewrecorder",
      handle: "brew-recorder",
      kind: "tool", cat: "brew",
      title: T("learn.title.brew_recorder", "Brew Recorder"),
      sub: T("learn.t_brewrecorder.sub", "Records weight and flow live from the scale. Save a brew as a recipe to follow later."),
      icon: "brew-recorder",
      room: "brew",
      for: ["microbalance-ti", "brew-control-ti"],
      guide: "brew-course",
      state: "ready",
      pin: 0.8,
      roles: { bar: 1.0, hobby: 0.95, roast: 0.6, q: 0.7, teach: 0.8, green: 0.3 }
    },
    {
      id: "t-grindermanager",
      handle: "grinder-manager",
      kind: "tool", cat: "grind",
      title: T("learn.title.grinder_manager", "Grinder Manager"),
      sub: T("learn.t_grindermanager.sub", "Keep a profile per grinder, per burr set."),
      icon: "grinder-manager",
      room: "brew",
      for: ["omni"],
      guide: "grinder-guide",
      state: "ready",
      pin: 0.6,
      roles: { hobby: 1.0, bar: 0.95, roast: 0.6, q: 0.5, teach: 0.55, green: 0.15 }
    },
    {
      id: "t-grinderconverter",
      handle: "grinder-converter",
      kind: "tool", cat: "grind",
      title: T("learn.title.grinder_converter", "Grinder Converter"),
      sub: T("learn.t_grinderconverter.sub", "Tells you what setting to use on another grinder to get a similar grind."),
      icon: "grinder-converter",
      room: "brew",
      for: ["omni"],
      guide: null,
      state: "soon",
      pin: 0.55,
      roles: { hobby: 1.0, bar: 0.9, roast: 0.5, q: 0.4, teach: 0.5, green: 0.1 }
    },
    {
      id: "t-brewratio",
      handle: "brew-ratio-calculator",
      kind: "tool", cat: "brew",
      title: T("learn.title.brew_ratio_calculator", "Brew Ratio Calculator"),
      sub: T("learn.t_brewratio.sub", "Dose, water and ratio, solved in whichever direction you need."),
      icon: "brew-ratio",
      room: "brew",
      for: [],
      guide: "brew-ratio",
      state: "ready",
      pin: 0.45,
      roles: { hobby: 0.9, bar: 0.8, roast: 0.4, teach: 0.7, q: 0.3, green: 0.15 }
    },
    {
      id: "t-beanmanager",
      handle: "bean-manager",
      kind: "tool", cat: "green",
      title: T("learn.title.bean_manager", "Bean Manager"),
      sub: T("learn.t_beanmanager.sub", "Your coffee inventory. Every measurement is attached to the bean it came from."),
      icon: "bean-manager",
      room: "farm",
      for: ["omix-plus", "coffmeter-m1"],
      guide: null,
      state: "soon",
      pin: 0.7,
      roles: { green: 1.0, q: 0.9, roast: 0.85, bar: 0.5, teach: 0.6 }
    },
    {
      id: "t-notes",
      handle: "notes-manager",
      kind: "tool", cat: "brew",
      title: T("learn.title.notes_manager", "Notes Manager"),
      sub: T("learn.t_notes.sub", "All your notes on every test and session, organized by Tool and searchable."),
      icon: "notes-manager",
      room: "brew",
      for: [],
      guide: null,
      state: "soon",
      pin: 0.3,
      roles: { bar: 0.7, q: 0.8, roast: 0.6, teach: 0.6, green: 0.4, comp: 0.85 }
    },
    {
      id: "t-flavour",
      handle: "flavor-thermometer",
      kind: "tool", cat: "brew",
      title: T("learn.title.flavor_thermometer", "Flavor Thermometer"),
      sub: T("learn.t_flavour.sub", "Controls Moment: create and follow profiles of the flavors to expect at each temperature."),
      icon: "flavor-thermometer",
      room: "brew",
      for: ["moment"],
      guide: null,
      state: "soon",
      pin: 0.1,
      /* Set for the competitor rather than derived. Moment is
         front and center for them and a tasting run the same way
         twice is half of what they are practising, so the derived
         weight — which comes off a cafe's use of it — would bury
         this at the bottom of their list. */
      roles: { q: 0.7, bar: 0.35, roast: 0.3, teach: 0.4, green: 0.35, comp: 0.95 }
    },

    /* ── Device guides ───────────────────────────── */
    {
      id: "d-omix",
      kind: "device",
      title: "Omix Plus",
      sub: T("learn.d_omix.sub", "Water activity, moisture, density, size and color distribution."),
      img: "os-farm-omix",
      room: "farm",
      for: ["omix-plus"],
      guide: "omix-guide",
      state: "ready",
      pin: 0.95,
      roles: { green: 1.0, q: 1.0, roaster: 0.85, roast: 0.75, teach: 0.6, bar: 0.2 }
    },
    {
      id: "d-omix-home",
      kind: "device",
      title: T("learn.title.omix_plus_for_home_roasters", "Omix Plus for home roasters"),
      sub: T("learn.d_omix_home.sub", "The short version, for roasting at home."),
      img: "os-roast-omix",
      room: "roast",
      for: ["omix-plus"],
      guide: "omix-home",
      state: "ready",
      pin: 0.25,
      roles: { home: 1.0, roast: 0.6, hobby: 0.5, green: 0.3, teach: 0.4, q: 0.2 }
    },
    {
      id: "d-omni",
      kind: "device",
      title: "Omni",
      sub: T("learn.d_omni.sub", "Roast color and particle distribution, in one device."),
      img: "os-roast-omni",
      room: "roast",
      for: ["omni"],
      guide: "omni-guide",
      state: "ready",
      pin: 0.9,
      roles: { roast: 1.0, bar: 0.85, q: 0.9, hobby: 0.8, teach: 0.8, green: 0.5 }
    },
    {
      id: "d-r2",
      kind: "device",
      title: "R2 Extract",
      sub: T("learn.d_r2.sub", "TDS in about four seconds, and what to do with it."),
      img: "os-brew-r2",
      room: "brew",
      for: ["r2-extract"],
      guide: "r2-guide",
      state: "ready",
      pin: 0.9,
      roles: { bar: 1.0, q: 0.95, roast: 0.7, hobby: 0.85, teach: 0.85, green: 0.4 }
    },
    {
      id: "d-rs",
      kind: "device",
      title: "RS Extract",
      sub: T("learn.d_rs.sub", "Zeroing, measuring every brew method, and brewing with Microbalance."),
      img: "os-brew-rs",
      room: "brew",
      for: ["rs-extract"],
      guide: "rs-guide",
      state: "ready",
      pin: 0.95,
      roles: { bar: 1.0, q: 0.95, roast: 0.7, hobby: 0.85, teach: 0.85, green: 0.4 }
    },
    {
      id: "d-mb",
      kind: "device",
      title: "Microbalance Ti",
      sub: T("learn.d_mb.sub", "Setting up the scale, and reading a flow trace."),
      img: "os-brew-mb",
      room: "brew",
      for: ["microbalance-ti", "brew-control-ti"],
      guide: null,
      state: "soon",
      pin: 0.5,
      roles: { bar: 0.95, hobby: 0.9, roast: 0.5, q: 0.6, teach: 0.7, green: 0.25 }
    },
    {
      id: "d-flux",
      kind: "device",
      title: "OmniFlux",
      sub: T("learn.d_flux.sub", "Mounting it, and Roast by Color while the drum turns."),
      img: "os-roast-flux",
      room: "roast",
      for: ["omniflux"],
      guide: null,
      state: "soon",
      pin: 0.6,
      roles: { roast: 1.0, q: 0.6, green: 0.4, teach: 0.6, bar: 0.2 }
    },
    {
      id: "d-airwave",
      kind: "device",
      title: "AirWave",
      sub: T("learn.d_airwave.sub", "Installing it, and what the catalyst actually does."),
      img: "os-roast-family",
      room: "roast",
      for: ["airwave"],
      guide: null,
      state: "soon",
      pin: 0.5,
      roles: { roast: 0.95, home: 1.0, teach: 0.7, green: 0.4, bar: 0.3, q: 0.2 }
    },
    {
      id: "d-a1",
      kind: "device",
      title: "CoffMeter A1",
      sub: T("learn.d_a1.sub", "A single Agtron value, and when that is enough."),
      img: "os-roast-a1",
      room: "roast",
      for: ["coffmeter-a1"],
      guide: null,
      state: "soon",
      pin: 0.35,
      roles: { roast: 0.8, home: 0.9, bar: 0.5, green: 0.4, q: 0.3, teach: 0.5 }
    },
    {
      id: "d-m1",
      kind: "device",
      title: "CoffMeter M1",
      sub: T("learn.d_m1.sub", "Moisture and density, and how to sample for them."),
      img: "os-farm-m1",
      room: "farm",
      for: ["coffmeter-m1"],
      guide: null,
      state: "soon",
      pin: 0.4,
      roles: { green: 0.95, q: 0.8, roast: 0.6, teach: 0.5, bar: 0.15 }
    },
    {
      id: "d-w1",
      kind: "device",
      title: "CoffMeter W1",
      sub: T("learn.d_w1.sub", "Water activity, measured before a lot goes into storage."),
      img: "os-farm-w1",
      room: "farm",
      for: ["coffmeter-w1"],
      guide: null,
      state: "soon",
      pin: 0.3,
      roles: { green: 0.9, q: 0.95, roast: 0.45, teach: 0.5, bar: 0.1 }
    }
  ];

  addCompWeights(ITEMS);

  /* ── Signals ──────────────────────────────────────────────── */

  function bag() {
    var c = window.OS_CART;
    return Array.isArray(c) ? c : [];
  }

  function owned() {
    try {
      var o = JSON.parse(localStorage.getItem("difluid_owned") || "[]");
      return Array.isArray(o) ? o : [];
    } catch (e) { return []; }
  }

  function opened() {
    try {
      var o = JSON.parse(localStorage.getItem("difluid_learn_opened") || "{}");
      return o && typeof o === "object" ? o : {};
    } catch (e) { return {}; }
  }

  function markOpened(id) {
    try {
      var o = opened();
      o[id] = Date.now();
      localStorage.setItem("difluid_learn_opened", JSON.stringify(o));
    } catch (e) {}
  }

  function roomOf(slugs) {
    /* Which room a set of product slugs belongs to, for the softer
       in-the-bag match. */
    var ROOM = {
      "omix-plus": "farm", "coffmeter-m1": "farm", "coffmeter-w1": "farm",
      "omni": "roast", "omniflux": "roast", "airwave": "roast", "coffmeter-a1": "roast",
      "r2-extract": "brew", "microbalance-ti": "brew", "brew-control-ti": "brew",
      "coaster": "brew", "moment": "brew"
    };
    var out = {};
    for (var i = 0; i < slugs.length; i++) if (ROOM[slugs[i]]) out[ROOM[slugs[i]]] = 1;
    return out;
  }

  /* ── Score ────────────────────────────────────────────────── */

  function roleScore(item, role) {
    var r = item.roles || {};
    if (typeof r[role] === "number") return r[role];
    var fam = FAMILY[role];
    if (fam && typeof r[fam] === "number") return r[fam];
    return 0.35;
  }

  function score(item, ctx) {
    var i, s;

    var role = roleScore(item, ctx.role);

    /* Bag. Direct hit if the guide is about something in the cart;
       a softer hit if it is merely the same room. */
    var bagHit = 0;
    for (i = 0; i < (item.for || []).length; i++) {
      if (ctx.bagSet[item.for[i]]) { bagHit = 1; break; }
    }
    if (!bagHit && item.room && ctx.bagRooms[item.room]) bagHit = 0.45;

    var ownHit = 0;
    for (i = 0; i < (item.for || []).length; i++) {
      if (ctx.ownSet[item.for[i]]) { ownHit = 1; break; }
    }

    /* Trade weight is the strongest product this guide covers. */
    var trade = 0;
    for (i = 0; i < (item.for || []).length; i++) {
      var t = ctx.conv[item.for[i]];
      if (typeof t === "number" && t > trade) trade = t;
    }

    s = W.role * role + W.bag * bagHit + W.owned * ownHit +
        W.trade * trade + W.pin * (item.pin || 0);

    /* A guide with no page yet still shows, but below one that opens. */
    if (item.state !== "ready") s *= 0.82;

    /* Something already read drops a little, so the shelf keeps
       offering the next thing rather than the same thing. */
    if (ctx.seen[item.id]) s *= 0.88;

    return s;
  }

  function rank(kind, role, opts) {
    opts = opts || {};
    var b = bag();
    var ctx = {
      role: role,
      bagSet: b.reduce(function (a, k) { a[k] = 1; return a; }, {}),
      bagRooms: roomOf(b),
      ownSet: owned().reduce(function (a, k) { a[k] = 1; return a; }, {}),
      conv: window.OS_CONV || TRADE,
      seen: opened()
    };

    var list = ITEMS.filter(function (it) {
      if (it.kind !== kind) return false;
      if (opts.cat && opts.cat !== "all" && it.cat !== opts.cat) return false;
      return true;
    });

    list = list.map(function (it) {
      var inBag = (it.for || []).some(function (k) { return ctx.bagSet[k]; });
      return { item: it, s: score(it, ctx), inBag: inBag };
    });

    list.sort(function (a, b2) {
      /* In the bag wins outright. Nothing outranks the thing they are
         about to pay for. */
      if (a.inBag !== b2.inBag) return a.inBag ? -1 : 1;
      if (b2.s !== a.s) return b2.s - a.s;
      return a.item.title.localeCompare(b2.item.title);
    });

    return list;
  }

  function cats() {
    return [
      { id: "all", label: T("learn.cat.all", "All") },
      { id: "brew", label: T("learn.cat.brew", "Brew") },
      { id: "roast", label: T("learn.cat.roast", "Roast") },
      { id: "grind", label: T("learn.cat.grind", "Grind") },
      { id: "green", label: T("learn.cat.green", "Green") }
    ];
  }

  /* Why this one is where it is — shown in the preview so the
     ranking can be argued with rather than guessed at. */
  function explain(item, role) {
    var b = bag();
    var bagSet = b.reduce(function (a, k) { a[k] = 1; return a; }, {});
    var rooms = roomOf(b);
    var parts = [];
    var r = roleScore(item, role);
    if (r >= 0.85) parts.push("core to this role");
    else if (r >= 0.6) parts.push("useful to this role");
    else if (r <= 0.3) parts.push("peripheral to this role");
    if ((item.for || []).some(function (k) { return bagSet[k]; })) parts.push("in the bag");
    else if (item.room && rooms[item.room]) parts.push("same room as the bag");
    var conv = window.OS_CONV || TRADE, t = 0;
    (item.for || []).forEach(function (k) { if (conv[k] > t) t = conv[k]; });
    if (t >= 0.8) parts.push("top seller");
    if (item.state !== "ready") parts.push("page not built yet");
    return parts.join(" · ");
  }

  return {
    ITEMS: ITEMS,
    FAMILY: FAMILY,
    TRADE: TRADE,
    WEIGHTS: W,
    rank: rank,
    cats: cats,
    explain: explain,
    markOpened: markOpened,
    opened: opened
  };
})();

/* This file is loaded by the section that needs it, not by os-head, so
   it arrives after os.js has already painted once and found no
   catalog. Ask for one more paint, and only when there is something
   to paint — on any other page os.js is not waiting for us.

   Two pages read this catalog: the Learn page, for its cards and its
   ranking, and the CoffeeOS page, for the "Where to start" card and
   the Tools grid. Both were blank on that second page until it started
   loading this file. */
if (window.OS && typeof window.OS.repaint === "function" &&
    document.querySelector("[data-os-learn-page], [data-os-coffeeos]")) {
  window.OS.repaint(window.OS.currentId());
}
